/**
 * Barra de filtros del tablero. Es un componente controlado: el estado de los filtros vive en
 * la página y esta barra solo lo muestra y avisa de los cambios.
 *
 * Diseño: buscador ancho + botón «Filtros» (con la cantidad de filtros activos) que abre un panel
 * con TODOS los filtros: Prioridad, Tipo, Proyecto, Responsable, Estado (abiertas por defecto),
 * «Solo riego», «Solo bloqueadas» y «Solo atrasadas». Los filtros activos quedan como chips quitables en su propia fila, debajo, para que
 * el buscador y el botón no se muevan al filtrar. En el celular el panel es una hoja desde abajo.
 *
 * El panel es un diálogo no modal: Esc, clic fuera o «Listo» lo cierran; al abrir, el foco va al
 * primer select y, al cerrar con Esc o «Listo», vuelve al botón (con clic fuera, el foco queda donde
 * hiciste clic). Se mantiene en el DOM con `hidden` para no perder el foco ni el estado de los selects.
 *
 * «Limpiar filtros» / «Limpiar todo» dejan todo como al entrar (Estado en «Abiertas», sin casillas);
 * el texto del buscador no se toca.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  ETIQUETA_TIPO,
  PRIORIDADES,
  TIPOS,
  type Prioridad,
  type Proyecto,
  type Tipo,
  type Usuario,
} from '@cdtec-backlog/contrato';
import { Icono } from '../../componentes/Icono';
import { useEtiquetaPrioridad } from '../configuracion/hooks';
import { FILTROS_INICIALES, OPCIONES_ESTADO, type FiltroEstado, type FiltrosUI } from './api';
import './BarraFiltros.css';

interface Props {
  filtros: FiltrosUI;
  personas: Usuario[];
  proyectos?: Proyecto[];
  onCambio: (cambios: Partial<FiltrosUI>) => void;
}

/** Los filtros del panel, en el orden en que se muestran (panel y chips). */
type ClaveFiltro =
  | 'prioridad'
  | 'tipo'
  | 'proyecto'
  | 'responsable'
  | 'estado'
  | 'soloRiego'
  | 'soloBloqueadas'
  | 'soloAtrasadas';
const CLAVES: ClaveFiltro[] = [
  'prioridad',
  'tipo',
  'proyecto',
  'responsable',
  'estado',
  'soloRiego',
  'soloBloqueadas',
  'soloAtrasadas',
];
/** Las casillas: su chip dice el texto de la casilla, no «Nombre: valor». */
type ClaveCasilla = 'soloRiego' | 'soloBloqueadas' | 'soloAtrasadas';
const TEXTO_CASILLA: Record<ClaveCasilla, string> = {
  soloRiego: 'Solo riego',
  soloBloqueadas: 'Solo bloqueadas',
  soloAtrasadas: 'Solo atrasadas',
};
const esCasilla = (clave: ClaveFiltro): clave is ClaveCasilla => clave in TEXTO_CASILLA;
const NOMBRE_FILTRO: Record<ClaveFiltro, string> = {
  prioridad: 'Prioridad',
  tipo: 'Tipo',
  proyecto: 'Proyecto',
  responsable: 'Responsable',
  estado: 'Estado',
  soloRiego: 'Riego',
  soloBloqueadas: 'Bloqueadas',
  soloAtrasadas: 'Atrasadas',
};
/** Todo el panel como al entrar (el buscador no es parte del panel). */
const SIN_FILTROS: Pick<FiltrosUI, ClaveFiltro> = {
  prioridad: FILTROS_INICIALES.prioridad,
  tipo: FILTROS_INICIALES.tipo,
  proyecto: FILTROS_INICIALES.proyecto,
  responsable: FILTROS_INICIALES.responsable,
  estado: FILTROS_INICIALES.estado,
  soloRiego: FILTROS_INICIALES.soloRiego,
  soloBloqueadas: FILTROS_INICIALES.soloBloqueadas,
  soloAtrasadas: FILTROS_INICIALES.soloAtrasadas,
};

/** Un filtro está activo cuando difiere de su valor al entrar. */
const estaActivo = (filtros: FiltrosUI, clave: ClaveFiltro) =>
  filtros[clave] !== SIN_FILTROS[clave];

/** Cuántos filtros del panel están activos (lo que muestra el contador del botón). */
export const contarFiltrosActivos = (filtros: FiltrosUI) =>
  CLAVES.filter((clave) => estaActivo(filtros, clave)).length;

/** Una «píldora» con etiqueta y select; se marca `activo` cuando el valor no es el vacío. */
function Filtro({
  etiqueta,
  valor,
  onCambio,
  children,
  refSelect,
}: {
  etiqueta: string;
  valor: string;
  onCambio: (valor: string) => void;
  children: ReactNode;
  refSelect?: React.Ref<HTMLSelectElement>;
}) {
  return (
    <label className={`filtro${valor !== '' ? ' activo' : ''}`}>
      <span>{etiqueta}</span>
      <select ref={refSelect} value={valor} onChange={(e) => onCambio(e.target.value)}>
        {children}
      </select>
    </label>
  );
}

export function BarraFiltros({ filtros, personas, proyectos = [], onCambio }: Props) {
  const etiquetaPrioridad = useEtiquetaPrioridad();
  const [abierto, setAbierto] = useState(false);
  const boton = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const primerSelect = useRef<HTMLSelectElement>(null);
  const idPanel = useId();

  /** Texto que muestra el chip de un filtro activo (el nombre visible, no el código). */
  const textoChip = (clave: ClaveFiltro): string => {
    if (esCasilla(clave)) return TEXTO_CASILLA[clave];
    return `${NOMBRE_FILTRO[clave]}: ${valorVisible(clave)}`;
  };
  const valorVisible = (clave: Exclude<ClaveFiltro, ClaveCasilla>): string => {
    const valor = filtros[clave];
    switch (clave) {
      case 'estado':
        return OPCIONES_ESTADO.find((o) => o.valor === valor)?.etiqueta ?? valor;
      case 'prioridad':
        return etiquetaPrioridad(valor as Prioridad);
      case 'tipo':
        return ETIQUETA_TIPO[valor as Tipo];
      case 'proyecto':
        if (valor === 'sin-proyecto') return 'Sin proyecto';
        return proyectos.find((p) => String(p.id) === valor)?.nombre ?? valor;
      case 'responsable':
        if (valor === 'sin-asignar') return 'Sin asignar';
        return personas.find((p) => String(p.id) === valor)?.nombre ?? valor;
    }
  };

  const activos = CLAVES.filter((clave) => estaActivo(filtros, clave));

  const cerrar = (devolverFoco: boolean) => {
    setAbierto(false);
    if (devolverFoco) boton.current?.focus();
  };

  useEffect(() => {
    if (!abierto) return;
    primerSelect.current?.focus();
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Captura + preventDefault: la ficha abierta detrás ignora los Esc ya atendidos.
      e.preventDefault();
      setAbierto(false);
      boton.current?.focus();
    };
    const alPulsar = (e: MouseEvent) => {
      const destino = e.target as Node;
      if (!panel.current?.contains(destino) && !boton.current?.contains(destino)) {
        setAbierto(false);
      }
    };
    document.addEventListener('keydown', alTeclear, true);
    document.addEventListener('mousedown', alPulsar);
    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      document.removeEventListener('mousedown', alPulsar);
    };
  }, [abierto]);

  return (
    <div className="filtros" role="search">
      <div className="filtros__fila">
        <label className="buscar">
          <Icono nombre="lupa" />
          <input
            type="search"
            aria-label="Buscar tareas"
            placeholder="Buscar por título, persona o CDT-12"
            value={filtros.texto}
            onChange={(e) => onCambio({ texto: e.target.value })}
          />
        </label>
        <div className="filtros__ancla">
          <button
            ref={boton}
            type="button"
            className={`boton-filtros${activos.length > 0 ? ' activo' : ''}`}
            aria-expanded={abierto}
            aria-controls={idPanel}
            aria-haspopup="dialog"
            aria-label={activos.length > 0 ? `Filtros, ${activos.length} activos` : undefined}
            onClick={() => (abierto ? cerrar(false) : setAbierto(true))}
          >
            <Icono nombre="ajustes" />
            Filtros
            {activos.length > 0 && (
              <span className="boton-filtros__cuenta" aria-hidden="true">
                {activos.length}
              </span>
            )}
          </button>
          <div
            ref={panel}
            id={idPanel}
            role="dialog"
            aria-label="Filtros"
            className="filtros__panel"
            hidden={!abierto}
          >
            {/* Solo se ve en el celular, donde el panel es una hoja que tapa la barra. */}
            <p className="filtros__titulo" aria-hidden="true">
              Filtros
            </p>
            <Filtro
              etiqueta="Prioridad"
              valor={filtros.prioridad}
              refSelect={primerSelect}
              onCambio={(valor) => onCambio({ prioridad: valor as Prioridad | '' })}
            >
              <option value="">Todas</option>
              {PRIORIDADES.map((p) => (
                <option key={p} value={p}>
                  {etiquetaPrioridad(p)}
                </option>
              ))}
            </Filtro>
            <Filtro
              etiqueta="Tipo"
              valor={filtros.tipo}
              onCambio={(valor) => onCambio({ tipo: valor as Tipo | '' })}
            >
              <option value="">Todos</option>
              {TIPOS.map((t) => (
                <option key={t} value={t}>
                  {ETIQUETA_TIPO[t]}
                </option>
              ))}
            </Filtro>
            <Filtro
              etiqueta="Proyecto"
              valor={filtros.proyecto}
              onCambio={(valor) => onCambio({ proyecto: valor })}
            >
              <option value="">Todos</option>
              <option value="sin-proyecto">Sin proyecto</option>
              {proyectos.map((proyecto) => (
                <option key={proyecto.id} value={String(proyecto.id)}>
                  {proyecto.activo ? proyecto.nombre : `${proyecto.nombre} (inactivo)`}
                </option>
              ))}
            </Filtro>
            <Filtro
              etiqueta="Responsable"
              valor={filtros.responsable}
              onCambio={(valor) => onCambio({ responsable: valor })}
            >
              <option value="">Todos</option>
              <option value="sin-asignar">Sin asignar</option>
              {personas
                .filter((persona) => persona.activo)
                .map((persona) => (
                  <option key={persona.id} value={String(persona.id)}>
                    {persona.nombre}
                  </option>
                ))}
            </Filtro>
            <Filtro
              etiqueta="Estado"
              valor={filtros.estado === 'abiertas' ? '' : filtros.estado}
              onCambio={(valor) => onCambio({ estado: (valor || 'abiertas') as FiltroEstado })}
            >
              {OPCIONES_ESTADO.map((opcion) => (
                <option key={opcion.valor} value={opcion.valor === 'abiertas' ? '' : opcion.valor}>
                  {opcion.etiqueta}
                </option>
              ))}
            </Filtro>
            <label className={`filtro-casilla${filtros.soloRiego ? ' activo' : ''}`}>
              <input
                type="checkbox"
                checked={filtros.soloRiego}
                onChange={(e) => onCambio({ soloRiego: e.target.checked })}
              />
              <Icono nombre="gota" />
              Solo riego
            </label>
            <label className={`filtro-casilla${filtros.soloBloqueadas ? ' activo' : ''}`}>
              <input
                type="checkbox"
                checked={filtros.soloBloqueadas}
                onChange={(e) => onCambio({ soloBloqueadas: e.target.checked })}
              />
              <Icono nombre="candado" />
              Solo bloqueadas
            </label>
            <label className={`filtro-casilla${filtros.soloAtrasadas ? ' activo' : ''}`}>
              <input
                type="checkbox"
                checked={filtros.soloAtrasadas}
                onChange={(e) => onCambio({ soloAtrasadas: e.target.checked })}
              />
              <Icono nombre="calendario" />
              Solo atrasadas
            </label>
            <div className="filtros__pie">
              <button
                type="button"
                className="filtros__limpiar-panel"
                disabled={activos.length === 0}
                onClick={() => onCambio(SIN_FILTROS)}
              >
                Limpiar filtros
              </button>
              <button type="button" className="boton boton--primario" onClick={() => cerrar(true)}>
                Listo
              </button>
            </div>
          </div>
        </div>
      </div>

      {activos.length > 0 && (
        <div className="filtros__chips">
          {activos.map((clave) => (
            <span key={clave} className="chip-filtro">
              <span className="chip-filtro__texto">{textoChip(clave)}</span>
              <button
                type="button"
                aria-label={`Quitar filtro ${NOMBRE_FILTRO[clave]}`}
                title={`Quitar filtro ${NOMBRE_FILTRO[clave]}`}
                onClick={() => onCambio({ [clave]: SIN_FILTROS[clave] })}
              >
                <Icono nombre="x" tamano={12} />
              </button>
            </span>
          ))}
          {activos.length >= 2 && (
            <button
              type="button"
              className="filtros__limpiar"
              onClick={() => onCambio(SIN_FILTROS)}
            >
              Limpiar todo
            </button>
          )}
        </div>
      )}
    </div>
  );
}
